import {CheckCircle2, ChevronDown, Loader2, Mail, Send} from 'lucide-react'
import { useState } from 'react'
import FadeIn from '@/components/FadeIn'

const CONTACT_EMAIL = 'vn@nygma.io'
const CONTACT_TELEGRAM = '@slavique'

function ContactForm() {
  const [name, setName] = useState('')
  const [contact, setContact] = useState('')
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [showContacts, setShowContacts] = useState(false)

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!name.trim() || !contact.trim())
      return

    setStatus('sending')

    const subject = encodeURIComponent(`Заявка на SEO/GEO-продвижение от ${name}`)
    const body = encodeURIComponent(
      `Имя: ${name}\nКонтакт: ${contact}\n\nСообщение:\n${message || 'не указано'}`,
    )

    window.setTimeout(() => {
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`
      setStatus('sent')
    }, 600)
  }

  return (
    <section id="contact" className="bg-neutral-950 py-20 sm:py-28">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-emerald-400">
            Связаться с нами
          </span>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">
            Оставьте заявку на консультацию
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-neutral-400">
            Расскажите о своём проекте — обсудим цели и подберём подходящую стратегию SEO/GEO-продвижения
          </p>
        </FadeIn>

        <FadeIn delay={100} className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.2fr]">
          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={() => setShowContacts(prev => !prev)}
              className="flex items-center justify-center gap-2 rounded-2xl border border-neutral-800 bg-neutral-900/40 p-6 text-sm font-semibold text-white transition-colors hover:border-emerald-400/40"
            >
              {showContacts ? 'Скрыть контакты' : 'Показать контакты'}
              <ChevronDown className={`h-4 w-4 transition-transform ${showContacts ? 'rotate-180' : ''}`} />
            </button>

            {showContacts
              ? (
                  <>
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      className="group flex items-center gap-4 rounded-2xl border border-neutral-800 bg-neutral-900/40 p-6 transition-colors hover:border-emerald-400/40"
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-400/10">
                        <Mail className="h-5.5 w-5.5 text-emerald-400" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-neutral-500">Email</div>
                        <div className="mt-0.5 font-semibold text-white">{CONTACT_EMAIL}</div>
                      </div>
                    </a>

                    <a
                      href="https://t.me/slavique"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-4 rounded-2xl border border-neutral-800 bg-neutral-900/40 p-6 transition-colors hover:border-emerald-400/40"
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-400/10">
                        <Send className="h-5.5 w-5.5 text-emerald-400" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-neutral-500">Telegram</div>
                        <div className="mt-0.5 font-semibold text-white">{CONTACT_TELEGRAM}</div>
                      </div>
                    </a>
                  </>
                )
              : null}
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-neutral-800 bg-neutral-900/40 p-7"
          >
            <div className="flex flex-col gap-4">
              <div>
                <label htmlFor="name" className="text-sm font-medium text-neutral-300">
                  Имя
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Как вас зовут"
                  required
                  className="mt-1.5 w-full rounded-xl border border-neutral-800 bg-neutral-950 px-4 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
              </div>

              <div>
                <label htmlFor="contact" className="text-sm font-medium text-neutral-300">
                  Email или Telegram
                </label>
                <input
                  id="contact"
                  type="text"
                  value={contact}
                  onChange={e => setContact(e.target.value)}
                  placeholder="Как с вами связаться"
                  required
                  className="mt-1.5 w-full rounded-xl border border-neutral-800 bg-neutral-950 px-4 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
              </div>

              <div>
                <label htmlFor="message" className="text-sm font-medium text-neutral-300">
                  Сообщение
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Расскажите о вашем проекте"
                  rows={4}
                  className="mt-1.5 w-full resize-none rounded-xl border border-neutral-800 bg-neutral-950 px-4 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-sm font-semibold text-neutral-950 transition-colors hover:bg-emerald-300 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === 'sending'
                  ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Отправка...
                      </>
                    )
                  : status === 'sent'
                    ? (
                        <>
                          <CheckCircle2 className="h-4 w-4" />
                          Заявка отправлена
                        </>
                      )
                    : (
                        'Отправить заявку'
                      )}
              </button>

              {status === 'sent'
                ? (
                    <p className="text-center text-xs text-neutral-500">
                      Откроется почтовый клиент с заполненным письмом на {CONTACT_EMAIL}
                    </p>
                  )
                : null}
            </div>
          </form>
        </FadeIn>
      </div>
    </section>
  )
}

export default ContactForm
